// src/components/forms/CertificateForm.tsx — statutory certificates and their expiry.
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useBuildingOptions, usePropertyOptions, useUnitOptions, useVendorOptions } from '../../lib/options';
import { Button, Field, Input, Modal, Select } from '../ui';
import { FormError, FormGrid, Wide, nulled } from './common';

type C = Record<string, any>;

/**
 * The renewal interval each certificate actually runs on. Setting the issue date
 * fills the expiry from this, which is the difference between a register that is
 * maintained and one that quietly goes stale.
 */
const TYPES: Array<[string, string, number]> = [
  ['GAS_SAFETY', 'Gas safety (CP12) — annual', 12],
  ['EICR', 'Electrical installation (EICR) — 5 yearly', 60],
  ['EPC', 'Energy performance (EPC) — 10 yearly', 120],
  ['FIRE_RISK_ASSESSMENT', 'Fire risk assessment — annual', 12],
  ['LEGIONELLA', 'Legionella risk assessment — 2 yearly', 24],
  ['LIFT', 'Lift (LOLER) — 6 monthly', 6],
  ['CIVIL_DEFENCE', 'Civil defence (UAE) — annual', 12],
  ['DEWA', 'DEWA (UAE) — annual', 12],
];
const EPC_BANDS = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];
const addMonths = (iso: string, months: number) => {
  if (!iso) return '';
  const d = new Date(iso); d.setMonth(d.getMonth() + months); d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
};

export function CertificateForm({ open, onClose, certificate, propertyId }: { open: boolean; onClose: () => void; certificate?: C; propertyId?: string }) {
  const isNew = !certificate;
  const qc = useQueryClient();
  const properties = usePropertyOptions();
  const vendors = useVendorOptions();
  const [custom, setCustom] = useState(() => !!certificate && !TYPES.some(([t]) => t === certificate.certificateType));
  const [form, setForm] = useState(() => ({
    propertyId: certificate?.propertyId ?? propertyId ?? '', buildingId: certificate?.buildingId ?? '', unitId: certificate?.unitId ?? '',
    certificateType: certificate?.certificateType ?? 'GAS_SAFETY', reference: certificate?.reference ?? '',
    issuedOn: certificate?.issuedOn ?? new Date().toISOString().slice(0, 10),
    expiresOn: certificate?.expiresOn ?? addMonths(new Date().toISOString().slice(0, 10), 12),
    vendorId: certificate?.vendorId ?? '', documentUrl: certificate?.documentUrl ?? '', rating: certificate?.rating ?? '',
  }));
  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));
  const buildings = useBuildingOptions(form.propertyId);
  const units = useUnitOptions({ propertyId: form.propertyId }, !!form.propertyId);

  const months = TYPES.find(([t]) => t === form.certificateType)?.[2];
  const chooseType = (t: string) => set({ certificateType: t, expiresOn: months || TYPES.find(([x]) => x === t) ? addMonths(form.issuedOn, TYPES.find(([x]) => x === t)?.[2] ?? 12) : form.expiresOn });
  const chooseIssued = (d: string) => set({ issuedOn: d, expiresOn: months ? addMonths(d, months) : form.expiresOn });

  const body = () => ({
    ...(isNew ? { propertyId: form.propertyId } : {}),
    buildingId: nulled(form.buildingId), unitId: nulled(form.unitId),
    certificateType: form.certificateType.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_'),
    reference: nulled(form.reference), issuedOn: form.issuedOn, expiresOn: form.expiresOn,
    vendorId: nulled(form.vendorId), documentUrl: nulled(form.documentUrl), rating: nulled(form.rating),
  });

  const save = useMutation({
    mutationFn: async () => (isNew ? api.post<C>('/compliance', body()) : api.patch<C>(`/compliance/${certificate!.id}`, body())),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['/compliance'] }); void qc.invalidateQueries({ queryKey: ['compliance-summary'] }); onClose(); },
  });

  const invalidDates = !!form.issuedOn && !!form.expiresOn && form.expiresOn < form.issuedOn;

  return (
    <Modal open={open} onClose={onClose} size="lg" title={isNew ? 'New certificate' : 'Edit certificate'}
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={() => save.mutate()} disabled={!form.propertyId || !form.issuedOn || !form.expiresOn || invalidDates || save.isPending}>{save.isPending ? 'Saving…' : isNew ? 'Record certificate' : 'Save changes'}</Button></>}>
      <div className="space-y-5">
        <FormGrid>
          <Field label="Scheme"><Select value={form.propertyId} disabled={!isNew} onChange={(e) => set({ propertyId: e.target.value, buildingId: '', unitId: '' })} className="w-full"><option value="">Choose a scheme…</option>{(properties.data ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</Select></Field>
          <Field label="Building"><Select value={form.buildingId} onChange={(e) => set({ buildingId: e.target.value })} className="w-full" disabled={!form.propertyId}><option value="">Whole scheme</option>{(buildings.data ?? []).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}</Select></Field>
          <Wide><Field label="Unit" hint="A gas certificate belongs to a home; a fire risk assessment to the building"><Select value={form.unitId} onChange={(e) => set({ unitId: e.target.value })} className="w-full" disabled={!form.propertyId}><option value="">Not unit specific</option>{(units.data ?? []).map((u) => <option key={u.id} value={u.id}>{u.reference}</option>)}</Select></Field></Wide>

          <Wide>
            <Field label="Certificate type">
              {custom
                ? <Input value={form.certificateType} onChange={(e) => set({ certificateType: e.target.value })} placeholder="SPRINKLER_TEST" className="w-full" />
                : <Select value={form.certificateType} onChange={(e) => chooseType(e.target.value)} className="w-full">{TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</Select>}
              <button type="button" onClick={() => setCustom((c) => !c)} className="mt-1.5 text-[11.5px] text-bronze-600 hover:underline">{custom ? 'Choose from the list' : 'Something else…'}</button>
            </Field>
          </Wide>

          <Field label="Issued on"><Input type="date" value={form.issuedOn} onChange={(e) => chooseIssued(e.target.value)} className="w-full" /></Field>
          <Field label="Expires on" hint={months && !custom ? `Filled from the ${months}-month renewal cycle` : undefined}><Input type="date" value={form.expiresOn} onChange={(e) => set({ expiresOn: e.target.value })} className="w-full" /></Field>

          <Field label="Reference"><Input value={form.reference} onChange={(e) => set({ reference: e.target.value })} placeholder="CP12-2026-0042" className="w-full" /></Field>
          <Field label="Certified by"><Select value={form.vendorId} onChange={(e) => set({ vendorId: e.target.value })} className="w-full"><option value="">Not recorded</option>{(vendors.data ?? []).map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}</Select></Field>

          {form.certificateType === 'EPC'
            ? <Field label="EPC band" hint="E or better is required to let in England"><Select value={form.rating} onChange={(e) => set({ rating: e.target.value })} className="w-full"><option value="">Not recorded</option>{EPC_BANDS.map((b) => <option key={b} value={b}>{b}</option>)}</Select></Field>
            : <Field label="Rating or result"><Input value={form.rating} onChange={(e) => set({ rating: e.target.value })} className="w-full" /></Field>}
          <Field label="Document link"><Input value={form.documentUrl} onChange={(e) => set({ documentUrl: e.target.value })} placeholder="https://…" className="w-full" /></Field>
        </FormGrid>

        {invalidDates && <p className="text-[12.5px] text-red-600">The expiry date is before the issue date.</p>}
        {form.certificateType === 'EPC' && ['F', 'G'].includes(form.rating) && (
          <p className="rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-[12.5px] text-amber-800">Band {form.rating} is below the MEES minimum — this home cannot legally be let in England without an exemption.</p>
        )}
        <p className="text-[12px] leading-relaxed text-ink-400">Expiring certificates drive preventive work orders: use “Raise renewal jobs” on the Compliance page to turn the next 60 days into scheduled jobs.</p>
        <FormError error={save.error} />
      </div>
    </Modal>
  );
}
